/* Light-DOM reset for the surfaces this stylesheet is the only styling for
   (item #8705, decision #5072).

   The two shadow mounts supply this themselves (SHADOW_SURFACE_RESET_CSS): no
   host reset can cross a shadow boundary, so every element there falls back to
   the UA defaults. The overlay is the same defect wearing the other case. It
   portals to document.body and is styled by this sheet, and this sheet declared
   box-sizing nowhere -- so the overlay had always been riding whatever reset the
   customer's page happened to declare, and on a page that declares none it
   rendered content-box. Measured on bare-host.html against the built bundle:
   .vo-search-search-input-wrapper 574px inside a 570px content box, over by
   exactly its 2px borders, and the header at 106px against its 75px flex basis.

   Scoped to the widget's own roots, never `*`: this sheet is injected into the
   customer's <head>, so a bare universal selector would restyle their page.

   Every selector is :where()-rooted with a universal descendant half, so the
   whole block sits at specificity 0 and loses every tie -- the attribution
   link's margin-left, the tag row's margin-bottom and the rest keep applying
   wherever they are declared in this file. */
:where(.vo-search-modal-container),
:where(.vo-search-modal-container) *,
:where(.vo-search-search-modal),
:where(.vo-search-search-modal) *,
:where(.vo-search-tile-overlay-inner),
:where(.vo-search-tile-overlay-inner) * {
    box-sizing: border-box;
    margin: 0;
}

/* Search container */
.visitingobjects-search {
    position: relative;
}

/* Modal container */
.vo-search-modal-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

.vo-search-modal-backdrop {
  --bs-backdrop-zindex: 1050;
  --bs-backdrop-bg: #fff;
  --bs-backdrop-opacity: 0.95;
}

/* Base styling for both modal and non-modal */
.vo-search-search-modal {
    background-color: var(--background-color, #fff);
    color: var(--text-color, #374151);
    border-radius: 8px;
    box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.08));
    border: 1px solid var(--border-color, #e5e7eb);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    --primary-color: #4b5563;
    transition: opacity 0.3s ease, transform 0.3s ease;
    width: 100%;
    height: 80vh;
    --header-height: 75px;
    --footer-height: 75px;
}

/* ============================================
   Glass Morphism for VO Dark Theme
   Distinctive purple-tinted dark theme with glowing effects
   ============================================ */

.vo-search-search-modal[data-theme="vo-dark"] {
    backdrop-filter: blur(var(--backdrop-blur, 16px));
    -webkit-backdrop-filter: blur(var(--backdrop-blur, 16px));
    box-shadow:
        var(--shadow-lg, 0 20px 40px rgba(139, 79, 255, 0.2)),
        0 0 60px rgba(139, 79, 255, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.05);
    border: 1px solid var(--surface-border-color, rgba(139, 79, 255, 0.35));
}

.vo-search-search-modal[data-theme="vo-dark"] .vo-search-search-input-wrapper {
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    --vo-search-bar-border-width: 1px;
    border: var(--vo-search-bar-border-width, 2px) solid rgba(139, 79, 255, 0.2);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.vo-search-search-modal[data-theme="vo-dark"] .vo-search-search-result:hover {
    background-color: var(--result-hover-bg, rgba(139, 79, 255, 0.08));
}

.vo-search-search-modal[data-theme="vo-dark"] .vo-search-search-result:active {
    background-color: var(--result-active-bg, rgba(139, 79, 255, 0.12));
}

/* ============================================
   Glass Morphism for VO Light Theme
   Distinctive purple-tinted light theme with subtle glowing effects
   ============================================ */

.vo-search-search-modal[data-theme="vo-light"] {
    backdrop-filter: blur(var(--backdrop-blur, 12px));
    -webkit-backdrop-filter: blur(var(--backdrop-blur, 12px));
    box-shadow:
        var(--shadow-lg, 0 15px 35px rgba(113, 44, 249, 0.12)),
        0 0 40px rgba(113, 44, 249, 0.06),
        inset 0 1px 0 rgba(255, 255, 255, 0.8);
    border: 1px solid var(--surface-border-color, rgba(113, 44, 249, 0.2));
}

.vo-search-search-modal[data-theme="vo-light"] .vo-search-search-input-wrapper {
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    --vo-search-bar-border-width: 1px;
    border: var(--vo-search-bar-border-width, 2px) solid rgba(113, 44, 249, 0.15);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
}

.vo-search-search-modal[data-theme="vo-light"] .vo-search-search-result:hover {
    background-color: var(--result-hover-bg, rgba(113, 44, 249, 0.04));
}

.vo-search-search-modal[data-theme="vo-light"] .vo-search-search-result:active {
    background-color: var(--result-active-bg, rgba(113, 44, 249, 0.08));
}

/* Inline mode - override height constraints */
.vo-search-search-modal:not(.vo-search-modal-enabled) {
    /* The in-page surface's height, and the one thing a host embedding it in a
       slot of its own needs to say (item #8772). A custom property inherits
       across a shadow boundary, which a host rule reaching for
       `.vo-search-inpage` cannot -- and the default is what the surface has
       always been, so an embed that sets nothing does not move. */
    height: var(--vo-search-surface-height, 380px);
}

/* Search header - fixed size */
.vo-search-search-header {
    padding: 15px;
    border-bottom: 1px solid var(--border-color, #e0e0e0); /* Respects user border color config */
    position: relative;
    flex: 0 0 var(--header-height);
    background-color: var(--background-color, #fff);
    color: var(--text-color, #222);
    z-index: 2;
}

/* Search body - flexible with constraints */
.vo-search-search-body {
    position: relative;
    display: flex;
    flex-direction: column;
    background-color: var(--background-color, #fff);
    color: var(--text-color, #222);
    max-height: calc(80vh - var(--header-height) - var(--footer-height));
    min-height: 200px;
    overflow: hidden;
    flex: 1 1 auto;
    overflow-y: auto;
}

/* Search results - scrollable content */
.vo-search-search-results {
    flex: 1 1 auto;
    position: relative;
    padding: 0;
    margin: 0;
    list-style: none;
}

/* Search footer - fixed size */
.vo-search-search-footer {
    padding: 12px 16px;
    border-top: 1px solid var(--border-color, #e0e0e0);
    display: flex;
    justify-content: flex-end;
    align-items: center;
    height: 48px;
    box-sizing: border-box;
    background-color: var(--background-color, #fff);
    color: var(--text-color, #c5c8cc);
    font-size: 15px;
}

.vo-search-search-footer a {
    text-decoration: none;
    cursor: pointer;
}

.vo-search-search-footer a:hover {
    color: var(--primary-color, var(--bs-violet));
}

/* Modal-specific styles */
.vo-search-modal-enabled .vo-search-search-modal {
    transform: scale(0.75);
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0.75);
    /* Host-relative, not viewport-relative. 100vw measured the viewport
       whatever box the overlay was scoped into, so the card came out at exactly
       600px on every desktop viewport -- overflowing the landing's 579px demo
       window and ignoring the 820px one. min() resolves against the card's own
       containing block and keeps 600px as the desktop cap. */
    width: min(100%, 600px);
}

.vo-search-modal-enabled .vo-search-search-modal.vo-search-active {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
    pointer-events: auto;
}

.vo-search-search-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--backdrop-color, rgba(0, 0, 0, 0.7));
    backdrop-filter: blur(3px);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
}

.vo-search-search-modal-backdrop.vo-search-active {
    opacity: 1;
    pointer-events: auto;
}

/* Search modal trigger button */
.vo-search-search-modal-trigger {
    position: relative;
    z-index: 1; /* Lower z-index than modal (which uses z-index: 10-11) */
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--trigger-size, 20px);
    height: var(--vo-search-trigger-height, var(--trigger-size, 20px));
    border-radius: var(--trigger-border-radius, 0);
    border: var(--trigger-border-width, 0) solid transparent;
    background-color: var(--trigger-background, transparent);
    box-shadow: var(--trigger-shadow, none);
    cursor: pointer;
    transition: transform 0.2s ease;
    padding: 0;
    margin: 0;
    top: var(--vo-search-trigger-offset-block, 2px);
    margin-right: var(--vo-search-trigger-margin-inline-end, 10px);
}

/* Ghost Template media queries */
/*  Source template */
@media (max-width: 767px) {
  .gh-navigation-actions .vo-search-search-modal-trigger {
      display: none;
  }
}

@media (min-width: 768px) {
  .gh-navigation-brand .vo-search-search-modal-trigger {
      display: none;
  }
}

/*  Casper template */
@media (max-width: 767px) {
  .gh-head-actions .vo-search-search-modal-trigger {
      display: none;
  }
}

@media (min-width: 768px) {
  .gh-head-brand .vo-search-search-modal-trigger {
      display: none;
  }
}

/* Trigger button positions */
.vo-search-search-modal-trigger.vo-search-position-top-right {
    top: 20px;
    right: 20px;
}

.vo-search-search-modal-trigger.vo-search-position-top-left {
    top: 20px;
    left: 20px;
}

.vo-search-search-modal-trigger.vo-search-position-bottom-right {
    bottom: 20px;
    right: 20px;
}

.vo-search-search-modal-trigger.vo-search-position-bottom-left {
    bottom: 20px;
    left: 20px;
}

.vo-search-search-modal-trigger:hover {
    transform: var(--trigger-hover-transform, scale(1.05));
    background-color: var(--trigger-hover-background, transparent);
    box-shadow: var(--trigger-hover-shadow, none);
}

.vo-search-search-modal-trigger:active {
    transform: var(--trigger-active-transform, scale(0.98));
    box-shadow: var(--trigger-active-shadow, none);
}

.vo-search-search-modal-trigger svg {
    width: var(--trigger-icon-size, 30px);
    height: var(--trigger-icon-size, 30px);
    color: var(--modal-trigger-icon-color, #ffffff);
    fill: currentColor;
    transition: color 0.2s ease, fill 0.2s ease;
    max-width: 100%;
    max-height: 100%;
}

/* Custom icon color support */
.vo-search-search-modal-trigger[data-icon-color] svg {
    color: var(--trigger-icon-color);
    stroke: var(--trigger-icon-color);
}

/* Custom icon content support for replacing the default icon */
.vo-search-search-modal-trigger[data-icon-content] svg {
    display: none;
}

.vo-search-search-modal-trigger[data-icon-content]::before {
    content: attr(data-icon-content);
    font-family: var(--trigger-icon-font, inherit);
    font-size: var(--trigger-icon-font-size, 20px);
    color: var(--primary-color, #712cf9);
}

/* Custom styles for embedding the search button */
.vo-search-embedded-search-trigger {
    position: relative;
    top: auto;
    right: auto;
    margin: 0 auto;
    display: inline-flex;
}

/* --vo-search-bar-inner-radius is the radius of this box's PADDING corner -- the
   hole the input and the submit button sit in -- which is this box's own radius
   less the width of the border drawn around it. It has to be computed because
   both terms move: the radius is host-tunable and the themed variants below drop
   the border to 1px.

   `overflow: hidden` used to make the subtraction unnecessary by clipping the
   leaves to that corner, and it was removed here (item #17191) because it was
   doing two unrelated jobs -- rounding corners and clipping content -- and only
   the first was wanted. The second is what would cut off the attached panel once
   item #17166 moves it into the field box. Same conflation item #17162 split on
   the panel itself, one level up. */
.vo-search-search-input-wrapper {
    --vo-search-bar-inner-radius: max(0px, calc(var(--vo-search-bar-radius, 8px) - var(--vo-search-bar-border-width, 2px)));
    position: relative;
    border: var(--vo-search-bar-border-width, 2px) solid var(--primary-color);
    /* Host-tunable so a nav slot can match its own control radius; the default
       is what the bar has always rendered. */
    border-radius: var(--vo-search-bar-radius, 8px);
    display: flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    padding: 0;
    margin: 0;
}

/* The input is the visually dominant element of the attached surface, and it
   sets outline:none -- so focus has to read on the box the user is looking at. */
.vo-search-search-input-wrapper:focus-within {
    box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb, 113, 44, 249), 0.25);
}

/* The field box (item #16450). The magnifier and the clear button are absolutely
   positioned and used to resolve against the wrapper; with an optional submit
   button as a flex sibling that would put the clear control on top of it. With
   no submit button this box spans the whole wrapper, so it is the same
   containing block those two had before and nothing moves. */
.vo-search-search-input-field {
    position: relative;
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
}

/* The optional in-input submit button (item #16450). Off by default, so this
   rule paints nothing on an instance that has not enabled one.

   The fill is read in `background` shorthand position rather than
   `background-color`, which is the whole reason it needs its own custom
   property: a gradient is a CSS property value that cannot ride a colour token
   (decision #5088). The fallback is the accent, so an enabled button with no
   configured fill is already themed. Item #17167 adds the strict grammar the
   stored value is validated against before it can be written. */
.vo-search-search-submit {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex: 0 0 auto;
    align-self: stretch;
    border: none;
    outline: none;
    cursor: pointer;
    padding: 0 16px;
    font-family: inherit;
    font-size: var(--vo-search-submit-font-size, 15px);
    font-weight: 600;
    white-space: nowrap;
    color: var(--vo-search-submit-color, #fff);
    background: var(--vo-search-submit-fill, var(--primary-color, #712cf9));
    border-start-end-radius: var(--vo-search-bar-inner-radius);
    border-end-end-radius: var(--vo-search-bar-inner-radius);
}

/* The button sets outline:none like the input beside it, so focus has to read
   somewhere. Inset rather than a ring: the button is flush with the wrapper's
   trailing edge, so an outer ring would paint over the border it sits against.
   (It used to be that the wrapper clipped it away outright; item #17191 moved
   the corner rounding onto the leaves and the clip is gone, but a ring drawn
   outside a flush edge still reads as a smear rather than a focus state.) */
.vo-search-search-submit:focus-visible {
    box-shadow: inset 0 0 0 2px var(--vo-search-submit-color, #fff);
}

.vo-search-search-icon {
    padding: 0 15px;
    color: var(--input-icon-color, #888);
    width: 20px;
    height: 20px;
    margin-left: 8px;
}

.vo-search-search-input {
    flex: 1;
    border: none;
    /* The fourth value is the search icon's clearance. It was an inline
       paddingLeft on the element until item #16444, where it outranked this
       whole block; it is chrome, not a computed value, so it lives here. */
    /* Clearance for the leading magnifier, which is optional (item #16449).
       Emitted only when the glyph is off; unset resolves to today's 40px. */
    padding: 12px 0 12px var(--vo-search-input-leading-padding, 40px);
    /* Host-tunable so a page whose type is larger can scale the bar to match,
       the same convention as --vo-search-bar-radius above. The fallbacks are
       what the input already rendered, so an embed that sets neither does not
       move: `auto` because item #16444 removed the fixed 40px height and left
       the padding to size the box. Item #16445. */
    font-size: var(--vo-search-input-font-size, 18px);
    height: var(--vo-search-input-height, auto);
    outline: none;
    width: 100%;
    border-width: 2px;
    background-color: var(--input-background-color, #fff);
    color: var(--input-text-color, #222);
    /* Logical, not physical: an RTL instance's leading edge is the right one, and
       physical corners would round the wrong end -- the same reason the shadow
       reset spells alignment `initial` rather than `left` (item #17159). */
    border-start-start-radius: var(--vo-search-bar-inner-radius);
    border-end-start-radius: var(--vo-search-bar-inner-radius);
}

/* The submit button is optional and off by default, so on most instances the
   input IS the whole field box and has to round the trailing end too.
   `:last-child` reads that off the DOM -- the button is either rendered after the
   field box or it is not -- rather than off a second copy of the config flag that
   decides it. */
.vo-search-search-input-field:last-child .vo-search-search-input {
    border-start-end-radius: var(--vo-search-bar-inner-radius);
    border-end-end-radius: var(--vo-search-bar-inner-radius);
}

.vo-search-search-input::placeholder {
    color: var(--input-placeholder-color, #9ca3af);
    opacity: 1;
}

.vo-search-search-input:focus {
    border-color: var(--primary-color, var(--bs-violet));
    box-shadow: none;
}

.vo-search-search-clear {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 24px;
    color: var(--input-icon-color, #888);
    padding: 0 15px;
    outline: none;
}

.vo-search-search-clear:hover {
    color: var(--input-icon-color, #888);
}

/* Loading indicator */
.vo-search-loading-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 0;
}

.vo-search-loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--border-color, rgba(0, 0, 0, 0.1));
    border-radius: 50%;
    border-top-color: var(--primary-color, var(--bs-violet));
    animation: vo-search-spin 1s linear infinite;
    margin-bottom: 15px;
}

@keyframes vo-search-spin {
    to {
        transform: rotate(360deg);
    }
}

/* Search results styles */
.vo-search-search-result {
    /* Item #17163. Fallbacks are today's row; the divider is switched off by a
       zero width rather than a second rule. */
    padding: var(--vo-search-result-padding, 15px);
    border-bottom: var(--vo-search-result-divider-width, 1px) solid var(--border-color, #e5e7eb);
    cursor: pointer;
    transition: background-color 0.2s ease;
}

/* Item i22789. Dividers separate rows; under the last one the pagination bar's
   border-top is the line, and both together drew 2px. */
.vo-search-search-result:last-child {
    border-bottom-width: 0;
}

.vo-search-search-result:hover {
    background-color: var(--result-hover-bg, #f9fafb);
}

.vo-search-search-result:active {
    background-color: var(--result-active-bg, #f3f4f6);
}

.vo-search-search-result a {
    text-decoration: none;
    color: inherit;
}

.vo-search-search-result-link {
    display: block;
    position: relative;
    color: inherit;
    text-decoration: none;
}

/* i22828: when any hit on the page has an image, every list row keeps the
   thumbnail column; an imageless row holds it with a neutral placeholder. */
.vo-search-search-result-link.has-thumb {
    display: grid;
    grid-template-columns: var(--vo-result-thumb-width, 56px) minmax(0, 1fr);
    gap: 12px;
    align-items: stretch;
}

.vo-search-search-result-text {
    min-width: 0;
}

/* i22831: the column width follows the enabled rows (set on the results
   container); the image fills the row's real height, never below square. */
.vo-search-result-thumbnail {
    display: block;
    width: 100%;
    height: 100%;
    min-height: var(--vo-result-thumb-width, 56px);
    align-self: stretch;
    object-fit: cover;
    border-radius: 6px;
}

.vo-search-result-thumbnail.is-empty {
    background-color: rgba(var(--text-color-rgb, 34,34,34), 0.08);
}

.vo-search-search-result-title {
    /* Declared, not left to the embedding page. This is an h3: without a
       margin here it took the host's heading rhythm (a Ghost theme's, on a
       customer site) in light DOM and the UA default 1em 0 inside a shadow
       root -- 36px of dead space per result row. */
    margin: 0;
    font-size: 18px;
    color: var(--text-color, #333);
    position: relative;
    padding-right: 25px;
    font-weight: 500;
    line-height: 1.2;
}

.vo-search-result-icons {
    position: absolute;
    right: 0;
    top: 0;
    display: flex;
}

.vo-search-search-result-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-bottom: 8px;
}

.vo-search-search-result-tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 15px;
    background-color: transparent;
    border: 1px solid var(--bs-violet);
    color: var(--bs-violet);
    font-size: 13px;
    font-weight: 400;
    white-space: nowrap;
}

.vo-search-search-result-excerpt {
    margin: 0;
    color: rgba(var(--text-color-rgb, 34,34,34), 0.95);
    font-size: 14px;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: var(--vo-excerpt-lines, 2);
    line-clamp: var(--vo-excerpt-lines, 2);
    overflow: hidden;
}

.vo-search-highlighted {
    /* Item #17164. --highlight-color was emitted and read by nothing; the
       accent stays as its fallback so an instance that sets none does not
       move. */
    color: var(--highlight-color, var(--primary-color, var(--bs-violet)));
    text-decoration: none;
    /* Item #17165. The underline reads its own token first and falls back to
       the text colour, so an unset instance keeps one colour for both. */
    border: 0 solid var(--highlight-underline-color, var(--highlight-color, var(--primary-color, var(--bs-violet))));
    border-bottom-width: 2px;
    font-weight: 500;
}

.vo-search-no-results {
    padding: 40px 20px;
    text-align: center;
    color: var(--no-results-color, #888);
}

.vo-search-result-icon {
    margin-left: 5px;
    display: inline-block;
    vertical-align: middle;
    position: relative;
    font-size: 14px;
}

.vo-search-featured-icon {
    color: var(--featured-icon-color, #d97706);
}

.vo-search-locked-icon {
    color: var(--locked-icon-color, #6b7280);
}

.vo-search-members-icon {
    color: var(--members-icon-color, #6b7280);
}

.vo-search-error-message {
    padding: 20px;
    text-align: center;
    color: var(--error-text-color, #e53e3e);
    background-color: var(--error-bg-color, #fff5f5);
    border-radius: 4px;
    margin: 20px;
}

/* Pagination styles */
.vo-search-pagination-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 15px;
    gap: 10px;
    border-top: 1px solid var(--border-color, #e0e0e0); /* Respects user border color config */
}

.vo-search-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.vo-search-pagination-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    height: 36px;
    padding: 0 10px;
    border-radius: 4px;
    border: none;
    background-color: var(--pagination-button-bg-color, var(--primary-color, #4b5563));
    color: var(--pagination-icon-color, #fff);
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s ease, opacity 0.2s ease;
}

.vo-search-pagination-button:disabled,
.vo-search-pagination-button.vo-search-disabled {
    opacity: 0.25;
    cursor: not-allowed;
}

.vo-search-pagination-arrow {
    font-size: 18px;
    color: var(--pagination-icon-color, #fff);
}

.vo-search-attribution {
    color: var(--text-color, #888);
    font-size: 13px;
    display: flex;
    align-items: center;
}

.vo-search-attribution * {
    text-decoration: none;
    color: inherit;
}

.vo-search-attribution-link {
    font-weight: 500;
    margin-left: 5px;
    color: var(--primary-color, var(--bs-violet));
}

.vo-search-attribution-link:hover {
    text-decoration: underline;
}

.vo-search-sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
}

/* Adding a media query transition reset to prevent resize glitches */
@media (max-width: 600px), (max-height: 600px) {
    /* Override transition only for size/position properties */
    .vo-search-modal-enabled .vo-search-search-modal {
        /* Disable all transformations at mobile sizes */
        transform: none !important;
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        height: 100% !important;
        position: fixed !important;
        transition: opacity 0.3s ease;
        max-width: none !important;
        max-height: none !important;
        border-radius: 0 !important;
        border: none !important;
        margin: 0 !important;
    }

    .vo-search-modal-enabled .vo-search-search-modal.vo-search-active {
        /* Only keep opacity transition for appearing/disappearing */
        transition: opacity 0.3s ease;
        transform: none !important;
    }

    /*
      A scoped overlay is laid out against its placement and nothing else
      (decision #5067). The block above pins the sheet to the VIEWPORT, and a
      media query measures the viewport too -- the condition is max-width 600px
      OR max-height 600px, so an ordinary desktop window under 600px tall matches
      it. `position: fixed` then ignores the containing block the portal target
      establishes, and `!important` beats the inline containment the container
      and backdrop carry (index.jsx, item #8737), so the container stayed inside
      the placement while the sheet covered the whole page: two search
      presentations on screen at once (item #8758, decision #5084).

      Contained, the same fullscreen treatment fills the placement instead --
      every other declaration above already resolves against the host once the
      sheet is absolutely positioned, and the content wrapper is sized inline at
      100% rather than in viewport units.

      This is the light-DOM path, which the demo bundle takes; the identical rule
      in SCOPED_OVERLAY_CSS (responsive_styles.jsx) contains a scoped overlay
      inside a shadow root, where this stylesheet cannot reach. The selectors
      match so the drift guard compares them. The `!important` is this block's
      own, matched so this can beat it -- source order is what decides the tie.
    */
    .vo-search-overlay-scoped .vo-search-search-modal {
        position: absolute !important;
    }

    /* Modal container fullscreen styles.

       COMPOUND, not descendant: index.jsx renders the container with
       `vo-search-modal-container vo-search-modal-enabled` on the SAME element,
       and nothing above it carries the class -- the container is the top of the
       overlay. Written as a descendant selector this block matched nothing
       anywhere, so the card was styled full-bleed (no radius, no border,
       max-width none -- those selectors DO match, being real descendants) inside
       a container that still padded 20px and centred it. Every popup on every
       phone letterboxed. */
    .vo-search-modal-container.vo-search-modal-enabled {
        padding: 0;
        align-items: stretch;
        justify-content: stretch;
        overflow-y: auto;
    }

    /* Removing redundant styles that are now applied directly to modal */
    .vo-search-modal-enabled .vo-search-search-modal-content {
        height: 100vh;
        width: 100vw;
        max-width: 100vw;
        margin: 0;
        padding: 0;
        border-radius: 0;
        display: flex;
        flex-direction: column;
    }

    .vo-search-modal-enabled .vo-search-search-body {
        max-height: calc(100vh - var(--header-height) - var(--footer-height));
        min-height: 0;
        flex: 1;
    }

    .vo-search-modal-enabled .vo-search-search-results {
        padding-bottom: 20px;
    }

    .vo-search-modal-enabled .vo-search-search-modal-backdrop {
        background-color: rgba(0, 0, 0, 0.85);
    }

    /* Adjust header and titlebar for mobile */
    .vo-search-modal-enabled .vo-search-search-titlebar,
    .vo-search-modal-enabled .vo-search-search-header {
        padding: 10px;
        border-bottom-color: var(--border-color, rgba(0, 0, 0, 0.1));
    }

    .vo-search-modal-enabled .vo-search-search-close-button {
        width: 28px;
        height: 28px;
    }

    .vo-search-modal-enabled .vo-search-search-close-button svg {
        width: 18px;
        height: 18px;
    }

    /* Footer adjustments for mobile */
    .vo-search-modal-enabled .vo-search-search-footer {
        height: auto;
        min-height: 48px;
        padding: 12px;
    }
}

.vo-search-search-titlebar {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 10px 15px;
    background-color: var(--background-color, #fff);
    border-bottom: 1px solid var(--border-color, #e0e0e0);
    position: relative;
}

.vo-search-search-title {
    flex: 1;
    text-align: center;
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    color: var(--text-color, #333);
}

.vo-search-search-close-button {
    background: transparent;
    border: none;
    padding: 5px;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--text-color, #333);
    width: 30px;
    height: 30px;
    border-radius: 50%;
    transition: background-color 0.2s ease;
}

.vo-search-search-close-button:hover {
    background-color: rgba(var(--text-color-rgb, 34,34,34), 0.05);
}

.vo-search-search-close-button svg {
    width: 20px;
    height: 20px;
    stroke: currentColor;
    stroke-width: 2px;
    transition: stroke 0.2s ease;
}

/* Ghost search replacement */
.gh-search {
    display: inline-block;
    position: relative;
}

/* Hide Ghost's search results container */
.gh-search-container {
    display: none;
}

[data-replace-ghost-search="true"] .vo-search-search-modal-trigger {
    background: transparent;
    border: none;
    color: var(--modal-trigger-icon-color, currentColor);
    width: 24px;
    height: 24px;
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

/* ============ Tile mode (fullscreen tile overlay) ============ */
/* The tile layout portals into the light DOM inside the shared overlay
   (.vo-search-modal-container), so its surface styling lives here like the
   modal's. Grid columns/responsive collapse stay in TILE_GRID_CSS. */
.vo-search-tile-overlay-inner {
    background-color: var(--background-color, #fff);
    color: var(--text-color, #374151);
    border: 1px solid var(--border-color, #e5e7eb);
    border-radius: 8px;
    box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.08));
    width: 100%;
    max-width: 1100px;
    margin: 0 auto;
    height: 90vh;
}

.vo-search-tile-card {
    background-color: var(--background-color, #fff);
    border: 1px solid var(--border-color, #e5e7eb);
    border-radius: 8px;
    overflow: hidden;
    transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

.vo-search-tile-card:hover {
    box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.12));
    border-color: var(--primary-color, #4b5563);
}

.vo-search-tile-card-link {
    display: block;
    height: 100%;
    color: inherit;
    text-decoration: none;
}

.vo-search-tile-hero {
    aspect-ratio: 16 / 9;
    background-color: var(--border-color, #eef0f3);
}

.vo-search-tile-body {
    padding: 12px 14px;
}
